import * as R from 'ramda';
import { isJust, just } from 'maybeasy';
import { ExperienceResource } from '../Types';

export interface SortOption {
  value: SortOptionValue;
  label: string;
}

type SortOptionValue = 'trending-first' | 'alphabetical-asc' | 'alphabetical-desc';

export const trendingFirst: SortOption = {
  value: 'trending-first',
  label: 'Trending',
};

const alphabeticalAscending: SortOption = {
  value: 'alphabetical-asc',
  label: 'Title A-Z',
};

const alphabeticalDescending: SortOption = {
  value: 'alphabetical-desc',
  label: 'Title Z-A',
};

export const sortingOptions: SortOption[] = [
  trendingFirst,
  alphabeticalAscending,
  alphabeticalDescending,
];

export interface State {
  sortOption: SortOption;
}

export const updateSelectedOption = (sortOption: SortOption): State => ({
  sortOption,
});

const partitionExperiences = (experiences: ExperienceResource[]) =>
  R.partition((exp) => isJust(exp.payload.featured), experiences);

const compareSort = <T extends string | Date>(a: T, b: T) => {
  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }
  return 0;
};

export const sortExperiences = (
  sortOption: SortOption,
  experiences: ExperienceResource[],
): ExperienceResource[] => {
  switch (sortOption.value) {
    case 'trending-first':
      const [featuredExperiences, otherExperiences] = partitionExperiences(experiences);
      const sortedFeaturedExperiences = featuredExperiences.sort((a, b) => {
        return just({})
          .assign('aFeaturedDate', a.payload.featured)
          .assign('bFeaturedDate', b.payload.featured)
          .map(({ aFeaturedDate, bFeaturedDate }) => compareSort(bFeaturedDate, aFeaturedDate))
          .getOrElseValue(0);
      });

      const sortedOtherExperiences = otherExperiences.sort((a, b) =>
        compareSort(a.payload.title.text, b.payload.title.text),
      );

      return sortedFeaturedExperiences.concat(sortedOtherExperiences);
    case 'alphabetical-asc':
      return experiences.sort((a, b) => compareSort(a.payload.title.text, b.payload.title.text));
    case 'alphabetical-desc':
      return experiences.sort((a, b) => compareSort(b.payload.title.text, a.payload.title.text));
  }
};
